import { useCallback } from 'react';
import { getBackendUrl } from '../config';

export const usePdfViewer = () => {
  
  // Processar URL para garantir que esteja no formato correto
  const processUrl = useCallback((url) => {
    if (!url) return '';
    
    try {
      // Se a URL já tem protocolo, retornar como está
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      
      const backendUrl = getBackendUrl() || '';
      const cleanBackendUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      
      if (cleanUrl.startsWith('/public/')) {
        return `${cleanBackendUrl}${cleanUrl}`;
      }
      
      return `${cleanBackendUrl}/public${cleanUrl}`;
    } catch (error) {
      console.error('❌ Erro ao processar URL:', error);
      return url;
    }
  }, []);

  // Verificar se uma URL é um PDF ou documento
  const isPdfUrl = (mediaUrl, body, mediaType) => {
    // Verifica pelo mediaType primeiro
    if (mediaType === "application/pdf") return true;
    if (mediaType === "application" || mediaType === "document") return true;
    
    // Verifica pela extensão do arquivo na URL
    if (mediaUrl) {
      const url = mediaUrl.toLowerCase();
      const docExtensions = [
        '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
        '.txt', '.csv', '.odt', '.ods', '.odp', '.rtf', '.xml', '.json',
        '.zip', '.rar', '.7z', '.tar', '.gz',
        '.pfx', '.p12', '.cer', '.crt', '.pem', '.der',
        '.exe', '.ofx', '.msg'
      ];
      for (const ext of docExtensions) {
        if (url.endsWith(ext) || url.includes(ext + '?') || url.includes(ext + '/')) {
          return true;
        }
      }
    }
    
    // Verifica no body como fallback
    if (body) {
      return body.toLowerCase().includes('.pdf');
    }
    
    return false;
  };

  // Validar se a URL parece ser válida
  const validatePdfUrl = useCallback((url, filename) => {
    if (!url || typeof url !== 'string') {
      return false;
    }

    // URLs muito curtas provavelmente são inválidas
    if (url.length < 5) {
      return false;
    }

    // Verificar se tem caracteres suspeitos
    if (url.includes('undefined') || url.includes('null')) {
      return false;
    }

    return true;
  }, []);

  // Fazer download do PDF via Blob (imune a cross-origin e não trava o navegador)
  const downloadPdf = useCallback(async (url, filename = 'documento.pdf') => {
    console.log('📥 Fazendo download:', { url, filename });
    
    if (!url) {
      console.error('❌ URL do arquivo não fornecida para download');
      return;
    }

    const processedUrl = processUrl(url);

    try {
      // 1. Tentar baixar via Blob (compatível com cross-origin e força o nome correto)
      const res = await fetch(processedUrl);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'documento.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      console.log('✅ Download via Blob concluído com sucesso');
    } catch (error) {
      console.warn('⚠️ Erro ao baixar via Blob, tentando via link direto:', error);
      
      try {
        const link = document.createElement('a');
        link.href = processedUrl;
        link.download = filename || 'documento.pdf';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('❌ Erro no fallback direto:', fallbackError);
        window.open(processedUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }, [processUrl]);

  // Extrair informações de um arquivo de uma mensagem
  const extractPdfInfoFromMessage = useCallback((message) => {
    console.log('🔍 Extraindo info do arquivo da mensagem:', message);
    
    const info = {
      url: message.mediaUrl || '',
      filename: 'documento',
      size: message.fileSize || null,
      mediaType: message.mediaType || '',
      isPdf: false
    };

    // Função para limpar nome de arquivo: remove timestamp do início e underscores
    const cleanFilename = (name) => {
      if (!name) return name;
      // Remove timestamp do início (ex: 1771451359335_)
      let cleaned = name.replace(/^\d{10,}_/, '');
      // Substitui underscores por espaços
      cleaned = cleaned.replace(/_/g, ' ');
      return cleaned.trim();
    };

    // 1. Tentar extrair da URL primeiro
    if (info.filename === 'documento' && info.url) {
      try {
        const urlParts = info.url.split('/');
        const urlFilename = urlParts[urlParts.length - 1];
        const cleanName = urlFilename.split('?')[0];
        const decodedName = decodeURIComponent(cleanName);
        
        if (decodedName && decodedName.includes('.')) {
          info.filename = cleanFilename(decodedName);
        }
      } catch (error) {
        console.warn('⚠️ Erro ao extrair filename da URL:', error);
      }
    }

    // 2. Se não obteve da URL, tentar extrair do body
    if (info.filename === 'documento' && message.body && typeof message.body === 'string') {
      let body = message.body.trim();
      // Remover prefixos de emoji comuns (📂, 📎, 📄)
      body = body.replace(/^(📂|📎|📄)\s*/, '').trim();
      // Ignorar textos genéricos
      const genericTexts = ['Outros anexos', 'Documento', 'Anexo', 'documento', 'anexo'];
      if (!genericTexts.includes(body) && body.length > 0 && body.length < 200) {
        info.filename = cleanFilename(body);
      }
    }

    // Verificar se é PDF
    info.isPdf = isPdfUrl(info.url, info.filename, info.mediaType);

    console.log('📄 Info extraída:', info);
    return info;
  }, [isPdfUrl]);

  return {
    // Ações principais
    downloadPdf,
    
    // Utilitários
    isPdfUrl,
    validatePdfUrl,
    extractPdfInfoFromMessage,
    processUrl,
  };
};